import { Trans, useLingui } from "@lingui/react/macro";
import { isMcpOAuthAppSession, mcpOAuthAppReturnUrl } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { completeMcpOauthReturn, MCP_OAUTH_CHANNEL } from "../lib/mcp-connect";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

// The window.open name set by the OAuth popup flow. Providers whose login
// pages send COOP sever window.opener mid-flow, but the window name survives,
// so it is the reliable "we are the popup" marker.
const POPUP_NAME = MCP_OAUTH_CHANNEL;
const CALLBACK_PATH = "/mcp/oauth/callback";

/** While this page is open, no request it makes names the address, which carries the code. */
function useNoReferrer() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.prepend(meta);
    return () => meta.remove();
  }, []);
}

export function McpOAuthCallbackPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // A sign-in the mobile app started goes back to the app, which completes it with its own
  // session. This page never completes it, even when this browser is signed in.
  const [appReturn, setAppReturn] = useState<{ url: string; failed: boolean } | null>(null);
  const handledState = useRef<string | null>(null);
  useNoReferrer();
  useEffect(() => {
    const code = params.get("code");
    const state = params.get("state");
    // Keep the one-time code out of the address bar and history: the values stay in memory only.
    // The router keeps its own copy of the address, so the app still renders this page.
    window.history.replaceState(window.history.state, "", CALLBACK_PATH);
    if (state && isMcpOAuthAppSession(state)) {
      if (handledState.current === state) return;
      handledState.current = state;
      const url = mcpOAuthAppReturnUrl(state, code ?? undefined);
      setAppReturn({ url, failed: !code });
      window.location.replace(url);
      return;
    }
    if (!code || !state) {
      setError(params.get("error_description") ?? t`OAuth authorization was cancelled.`);
      return;
    }
    if (handledState.current === state) return;
    handledState.current = state;
    void rpc.mcp.oauth
      .complete({ sessionId: state, code, state })
      .then(() => {
        const channel = new BroadcastChannel(POPUP_NAME);
        channel.postMessage({ type: "mcp-oauth-complete" });
        channel.close();
        if (window.name === POPUP_NAME) {
          setDone(true);
          window.close();
          return;
        }
        navigate(completeMcpOauthReturn(state) ?? "/app?mcp_oauth=connected", { replace: true });
      })
      .catch((err: unknown) => setError(errorText(err, t`Could not complete OAuth`)));
  }, [navigate, params, t]);
  if (appReturn) {
    return (
      <div className="grid min-h-screen place-items-center bg-background p-6 text-center">
        <div>
          <div className="text-lg text-foreground">
            {appReturn.failed ? (
              <Trans>OAuth connection failed</Trans>
            ) : (
              <Trans>Finishing MCP connection…</Trans>
            )}
          </div>
          {/* Some mobile browsers open an app address only after a tap. */}
          <Button
            type="button"
            className="mt-5"
            onClick={() => window.location.assign(appReturn.url)}
          >
            <Trans>Return to HrokBot</Trans>
          </Button>
        </div>
      </div>
    );
  }
  const showReturn = Boolean(error) && window.name !== POPUP_NAME;
  return (
    <div className="grid min-h-screen place-items-center bg-background p-6 text-center">
      <div>
        <div className="text-lg text-foreground">
          {error ? (
            <Trans>OAuth connection failed</Trans>
          ) : done ? (
            <Trans>Connected</Trans>
          ) : (
            <Trans>Finishing MCP connection…</Trans>
          )}
        </div>
        {error ? <p className="mt-2 max-w-md text-sm text-muted-foreground">{error}</p> : null}
        {showReturn ? (
          <Button type="button" className="mt-5" onClick={() => navigate("/app")}>
            <Trans>Return to HrokBot</Trans>
          </Button>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            {error || done ? (
              <Trans>You can close this window.</Trans>
            ) : (
              <Trans>You can close this tab if it does not redirect automatically.</Trans>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
